// ============================================
// DeferredPass - Base class for deferred lighting passes
// ============================================

import type { IRenderContext } from '../core/IRenderContext';
import type { Camera2D } from '../objects/Camera2D';
import type {
  RenderTargetHandle,
  TextureHandle,
  BufferHandle,
  PipelineHandle,
  ShaderHandle,
  ComputePipelineHandle,
} from '../types';
import { NULL_HANDLE, BlendMode } from '../types';

/**
 * Injection points specific to the deferred lighting pipeline.
 * These are called in order during a frame.
 */
export enum DeferredInjectionPoint {
  /** After GBuffer is rendered (albedo, normal, emission written). */
  AfterGBuffer = 100,
  /** After Forward+ light culling compute pass. */
  AfterLightCull = 200,
  /** After shadow map computation. */
  AfterShadows = 300,
  /** Before light accumulation (last chance before lit output). */
  BeforeLightAccum = 400,
  /** After light accumulation (lit image available). */
  AfterLightAccum = 500,
}

/**
 * Resources available to deferred passes during execution.
 */
export interface DeferredPassResources {
  /** Render context for GPU operations. */
  ctx: IRenderContext;
  /** Current camera. */
  camera: Camera2D;
  /** Viewport dimensions. */
  width: number;
  height: number;
  /** Time since start (seconds). */
  time: number;
  /** Delta time (seconds). */
  deltaTime: number;

  // ── GBuffer ──
  /** Albedo texture (rgba8). */
  albedoTexture: TextureHandle;
  /** World normal texture (rg16f, xy encoded). */
  normalTexture: TextureHandle;
  /** Emission texture (rgba8, rgb = emission color, a = emission mask). */
  emissionTexture: TextureHandle;
  /** GBuffer depth (if available). */
  depthTexture: TextureHandle | null;

  // ── Lighting Data ──
  /** Light uniform buffer (ambient, counts, screen size, inv VP). */
  lightUniformBuffer: BufferHandle;
  /** Storage buffer with all lights. */
  lightBuffer: BufferHandle;
  /** Tile light data from Forward+ culling. */
  tileLightBuffer: BufferHandle | null;
  /** Shadow atlas buffer (1D radial shadows). */
  shadowBuffer: BufferHandle | null;
  /** IES profile data buffer. */
  iesBuffer: BufferHandle | null;

  // ── Output ──
  /** Current output render target (null = backbuffer). */
  outputTarget: RenderTargetHandle | null;
  /** Whether output is HDR (rgba16float). */
  outputHDR: boolean;
}

/**
 * Base class for deferred lighting pipeline passes.
 *
 * Provides access to GBuffer textures, light buffers, and shadow data.
 * Override `onExecute()` to implement custom rendering.
 *
 * @example
 * ```typescript
 * class FogPass extends DeferredPass {
 *   constructor() {
 *     super('Fog', DeferredInjectionPoint.AfterLightAccum);
 *   }
 *
 *   protected onExecute(resources: DeferredPassResources): void {
 *     // Sample GBuffer, compute fog
 *   }
 * }
 * ```
 */
export abstract class DeferredPass {
  /** Unique name for this pass. */
  readonly name: string;

  /** Where in the deferred pipeline this pass executes. */
  readonly injectionPoint: DeferredInjectionPoint;

  /** Whether this pass is enabled. */
  enabled = true;

  /** Execution order within the same injection point (lower = earlier). */
  order = 0;

  /** Internal initialization state. */
  private _initialized = false;

  constructor(name: string, injectionPoint: DeferredInjectionPoint) {
    this.name = name;
    this.injectionPoint = injectionPoint;
  }

  /**
   * Initialize pass resources. Called once before first execution.
   */
  initialize(ctx: IRenderContext): void {
    if (this._initialized) return;
    this.onInitialize(ctx);
    this._initialized = true;
  }

  /**
   * Execute the pass with deferred resources.
   */
  execute(resources: DeferredPassResources): void {
    if (!this.enabled) return;
    if (!this._initialized) {
      this.initialize(resources.ctx);
    }
    this.onExecute(resources);
  }

  /**
   * Cleanup pass resources.
   */
  dispose(ctx: IRenderContext): void {
    if (!this._initialized) return;
    this.onDispose(ctx);
    this._initialized = false;
  }

  /**
   * Resize callback (viewport changed).
   */
  resize(width: number, height: number): void {
    this.onResize(width, height);
  }

  get isInitialized(): boolean {
    return this._initialized;
  }

  // ============================================
  // Override these in subclasses
  // ============================================

  protected onInitialize(ctx: IRenderContext): void {
    // Default: no-op
  }

  protected abstract onExecute(resources: DeferredPassResources): void;

  protected onDispose(ctx: IRenderContext): void {
    // Default: no-op
  }

  protected onResize(width: number, height: number): void {
    // Default: no-op
  }
}

/**
 * Manager for deferred passes in the lighting pipeline.
 */
export class DeferredPassManager {
  private passes: Map<DeferredInjectionPoint, DeferredPass[]> = new Map();
  private ctx: IRenderContext | null = null;

  /**
   * Set the render context.
   */
  setContext(ctx: IRenderContext): void {
    this.ctx = ctx;
  }

  /**
   * Add a deferred pass.
   */
  addPass(pass: DeferredPass): void {
    const point = pass.injectionPoint;
    let list = this.passes.get(point);
    if (!list) {
      list = [];
      this.passes.set(point, list);
    }

    // Insert sorted by order
    const insertIdx = list.findIndex(p => p.order > pass.order);
    if (insertIdx >= 0) {
      list.splice(insertIdx, 0, pass);
    } else {
      list.push(pass);
    }
  }

  /**
   * Remove a deferred pass.
   */
  removePass(pass: DeferredPass): boolean {
    const point = pass.injectionPoint;
    const list = this.passes.get(point);
    if (!list) return false;

    const idx = list.indexOf(pass);
    if (idx >= 0) {
      if (this.ctx) {
        pass.dispose(this.ctx);
      }
      list.splice(idx, 1);
      return true;
    }
    return false;
  }

  /**
   * Get a pass by name.
   */
  getPass(name: string): DeferredPass | null {
    for (const [_, list] of this.passes) {
      const pass = list.find(p => p.name === name);
      if (pass) return pass;
    }
    return null;
  }

  /**
   * Execute all passes at a specific injection point.
   */
  executePasses(point: DeferredInjectionPoint, resources: DeferredPassResources): void {
    const list = this.passes.get(point);
    if (!list || list.length === 0) return;

    for (const pass of list) {
      pass.execute(resources);
    }
  }

  /**
   * Check if any passes exist at an injection point.
   */
  hasPassesAt(point: DeferredInjectionPoint): boolean {
    const list = this.passes.get(point);
    return list !== undefined && list.length > 0;
  }

  /**
   * Notify all passes of a resize.
   */
  resize(width: number, height: number): void {
    for (const [_, list] of this.passes) {
      for (const pass of list) {
        pass.resize(width, height);
      }
    }
  }

  /**
   * Dispose all passes.
   */
  dispose(): void {
    if (!this.ctx) return;

    for (const [_, list] of this.passes) {
      for (const pass of list) {
        pass.dispose(this.ctx);
      }
    }
    this.passes.clear();
  }

  /**
   * Get total pass count.
   */
  get passCount(): number {
    let count = 0;
    for (const [_, list] of this.passes) {
      count += list.length;
    }
    return count;
  }
}
